import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink, useNavigate } from "react-router-dom";
import {
    FiLock,
    FiUnlock,
    FiStar,
    FiTrendingUp,
    FiFileText,
    FiArrowRight,
    FiBookmark,
    FiCalendar,
    FiUser,
    FiZap,
    FiCheck,
} from "react-icons/fi";

// Format date
const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export default function ResearchHome() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("all");
    const [subscriptionPlan, setSubscriptionPlan] = useState("free"); // free, basic, premium

    // Static Data - Featured Research
    const featuredResearch = [
        {
            id: 1,
            title: "Market Outlook 2024: Growth Opportunities in Indian Equity",
            category: "Market Analysis",
            author: "Research Team",
            date: "2024-01-15",
            isLocked: false,
            isFeatured: true,
            preview: "Comprehensive analysis of market trends and investment opportunities...",
            readTime: "8 min read",
        },
        {
            id: 2,
            title: "Sector Deep Dive: Technology & IT Services",
            category: "Sector Analysis",
            author: "Senior Analyst",
            date: "2024-01-12",
            isLocked: true,
            isFeatured: true,
            preview: "Detailed analysis of technology sector performance and future outlook...",
            readTime: "12 min read",
        },
        {
            id: 3,
            title: "Portfolio Rebalancing Strategy for Q1 2024",
            category: "Portfolio Strategy",
            author: "Investment Advisor",
            date: "2024-01-10",
            isLocked: true,
            isFeatured: true,
            preview: "Strategic recommendations for portfolio rebalancing in the new year...",
            readTime: "10 min read",
        },
    ];

    // Static Data - Research Feed
    const researchFeed = [
        {
            id: 1,
            title: "Large Cap Funds: Performance Review",
            category: "Fund Analysis",
            author: "Fund Research Team",
            date: "2024-01-14",
            isLocked: false,
            preview: "Analysis of top-performing large cap mutual funds...",
            readTime: "6 min read",
        },
        {
            id: 2,
            title: "Mid Cap Opportunities in 2024",
            category: "Market Analysis",
            author: "Research Team",
            date: "2024-01-13",
            isLocked: false,
            preview: "Exploring mid-cap investment opportunities...",
            readTime: "7 min read",
        },
        {
            id: 3,
            title: "Tax-Saving ELSS Funds Guide",
            category: "Tax Planning",
            author: "Tax Advisor",
            date: "2024-01-11",
            isLocked: true,
            preview: "Complete guide to ELSS funds for tax saving...",
            readTime: "9 min read",
        },
        {
            id: 4,
            title: "Small Cap Funds: Risk & Returns",
            category: "Fund Analysis",
            author: "Fund Research Team",
            date: "2024-01-09",
            isLocked: true,
            preview: "Understanding risks and returns in small cap funds...",
            readTime: "8 min read",
        },
        {
            id: 5,
            title: "SIP vs Lumpsum: Which is Better?",
            category: "Investment Strategy",
            author: "Investment Advisor",
            date: "2024-01-08",
            isLocked: false,
            preview: "Comparative analysis of SIP and lumpsum investments...",
            readTime: "5 min read",
        },
        {
            id: 6,
            title: "International Funds: Diversification Benefits",
            category: "Global Markets",
            author: "Global Research Team",
            date: "2024-01-07",
            isLocked: true,
            preview: "Benefits of investing in international mutual funds...",
            readTime: "11 min read",
        },
    ];

    // Categories
    const categories = [
        { id: "all", name: "All", count: researchFeed.length },
        { id: "market", name: "Market Analysis", count: 2 },
        { id: "fund", name: "Fund Analysis", count: 2 },
        { id: "strategy", name: "Investment Strategy", count: 1 },
        { id: "tax", name: "Tax Planning", count: 1 },
    ];

    const handleUpgradePlan = () => {
        navigate("/research/plans-billing");
    };

    const handleOpenFeed = (item) => {
        if (item.isLocked && subscriptionPlan === "free") {
            alert("This content requires a premium subscription. Please upgrade your plan.");
            return;
        }
        alert(`Opening: ${item.title}`);
    };

    const filteredFeed = activeTab === "all"
        ? researchFeed
        : researchFeed.filter((item) => {
            const categoryMap = {
                market: "Market Analysis",
                fund: "Fund Analysis",
                strategy: "Investment Strategy",
                tax: "Tax Planning",
            };
            return item.category === categoryMap[activeTab];
        });

    const canAccessContent = (isLocked) => {
        if (!isLocked) return true;
        return subscriptionPlan !== "free";
    };

    return (
        <>
            <ClientNavbar />
            <div className="page-content" style={{ background: "#f8fafc" }}>
                <style>{`
                .research-card {
                    transition: all 0.3s ease;
                    border: 1px solid #e9ecef;
                }
                .research-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.1) !important;
                    border-color: #1bafb4;
                }
                .locked-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%);
                    backdrop-filter: blur(2px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    z-index: 5;
                }
                .premium-badge {
                    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
                    color: white;
                    font-weight: 600;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .category-badge {
                    background: linear-gradient(135deg, #06b6a4 0%, #1bafb4 100%);
                    color: white;
                    font-weight: 500;
                    padding: 4px 10px;
                    border-radius: 4px;
                    font-size: 11px;
                }
                .tab-button {
                    transition: all 0.2s ease;
                    border-radius: 8px;
                }
                .tab-button:hover {
                    background-color: #f1f5f9;
                }
            `}</style>

                 
                        {/* Header */}
                        <div className="card shadow-sm p-4 mb-4">
                            <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                                <div>

                                    <h3 className="text-primary mb-2">Research Home</h3>
                                    <nav
                                        aria-label="breadcrumb"
                                        style={{ "--bs-breadcrumb-divider": "none" }}
                                    >
                                        <ol className="breadcrumb mb-0">
                                            <li className="breadcrumb-item">
                                                <NavLink to="/overview">Dashboard</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item">

                                                <NavLink to="/overview">Research Analysis</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item active">Research Home</li>
                                        </ol>
                                    </nav>
                                </div>
                                <div className="d-flex gap-2 align-items-center flex-wrap mt-2">
                                    {subscriptionPlan === "free" && (
                                        <button
                                            className="btn btn-primary btn-sm fw-semibold d-flex align-items-center"
                                            onClick={handleUpgradePlan}

                                        >
                                            <FiStar className="me-1" />
                                            Upgrade Plan
                                        </button>
                                    )}
                                    <span
                                        className={`badge px-3 py-2 fw-semibold ${subscriptionPlan === "free"
                                            ? "bg-secondary"
                                            : subscriptionPlan === "basic"
                                                ? "bg-info"
                                                : "bg-success"
                                            }`}
                                        style={{ fontSize: "12px" }}
                                    >
                                        {subscriptionPlan === "free"
                                            ? "Free Plan"
                                            : subscriptionPlan === "basic"
                                                ? "Basic Plan"
                                                : "Premium Plan"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Premium Upgrade Banner */}
                        {subscriptionPlan === "free" && (
                            <div
                                className="card mb-4 shadow-sm"

                            >
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                                        <div className="d-flex align-items-start">
                                            <div
                                                className="me-3 d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: "48px",
                                                    height: "48px",
                                                    borderRadius: "12px",
                                                    background: "#c8eeecbf",
                                                }}
                                            >
                                                <FiLock style={{ fontSize: "24px", color: "#39b2b4" }} />
                                            </div>
                                            <div>
                                                <h5 className="mb-1 fw-bold" >
                                                    Upgrade to unlock premium research content
                                                </h5>
                                                <p className="mb-0 " >
                                                    Access exclusive reports, in-depth analysis, and expert insights
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            className="btn btn-primary mt-3 mt-md-0 fw-semibold px-4"
                                            onClick={handleUpgradePlan}
                                        // style={{
                                        //     background: "linear-gradient(135deg, #1bafb4 0%, #06b6a4 100%)",
                                        //     border: "none",
                                        //     boxShadow: "0 4px 12px rgba(27, 175, 180, 0.3)",
                                        // }}
                                        >
                                            View Plans
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Featured Section */}
                        <div className="card mb-4 shadow-sm">
                            <div className="d-flex align-items-center mb-3">
                                <div
                                    className="me-2 d-flex align-items-center justify-content-center"
                                    style={{
                                        width: "48px",
                                        height: "48px",
                                        borderRadius: "10px",
                                        background: "#c8eeecbf",
                                    }}
                                >
                                    <FiStar style={{ color: "#39b2b4", fontSize: "20px" }} />
                                </div>
                                <h4 className="text-primary " >Featured Research</h4>
                            </div>
                            <div className="row g-4">
                                {featuredResearch.map((item) => {
                                    const isLocked = item.isLocked && !canAccessContent(item.isLocked);
                                    return (
                                        <div key={item.id} className="col-md-4">
                                            <div
                                                className="card h-100 research-card position-relative border-0 shadow-sm"
                                                style={{ borderRadius: "12px", overflow: "hidden" }}
                                            >
                                                {isLocked && (
                                                    <div className="locked-overlay">
                                                        <div className="text-center p-3">
                                                            <div
                                                                className="mb-3 d-inline-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: "64px",
                                                                    height: "64px",
                                                                    borderRadius: "50%",
                                                                    background: "#c8eeecbf",
                                                                }}
                                                            >
                                                                <FiLock style={{ fontSize: "28px", color: "#39b2b4" }} />
                                                            </div>
                                                            <h5 className="card-title  mb-3" style={{ fontSize: "18px", lineHeight: "1.4" }}>Premium Content</h5>
                                                            <p className="small text-muted mb-3">
                                                                Upgrade to unlock this exclusive research
                                                            </p>
                                                            <button
                                                                className="btn btn-primary"
                                                                onClick={handleUpgradePlan}
                                                                style={{
                                                                    background: "linear-gradient(135deg, #1bafb4 0%, #06b6a4 100%)",
                                                                    border: "none",
                                                                    borderRadius: "8px",
                                                                    padding: "10px",
                                                                }}
                                                            >
                                                                Unlock with Premium
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="card-body p-4">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <span className="category-badge">{item.category}</span>
                                                        {isLocked ? (
                                                            <span className="premium-badge">
                                                                {/* <FiLock className="me-1" style={{ fontSize: "10px" }} /> */}
                                                                Premium
                                                            </span>
                                                        ) : (
                                                            <span
                                                                className="badge bg-success"
                                                            >
                                                                {/* <FiUnlock className="me-1" style={{ fontSize: "10px" }} /> */}
                                                                Free
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h5 className="card-title  mb-3" style={{ fontSize: "18px", lineHeight: "1.4" }}>
                                                        {item.title}
                                                    </h5>
                                                    <p className="card-text text-muted mb-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                                                        {item.preview}
                                                    </p>
                                                    <div className="d-flex flex-wrap gap-3 mb-3" style={{ fontSize: "12px" }}>
                                                        <div className="d-flex align-items-center text-muted">
                                                            <FiUser className="me-1" style={{ fontSize: "14px" }} />
                                                            {item.author}
                                                        </div>
                                                        <div className="d-flex align-items-center text-muted">
                                                            <FiCalendar className="me-1" style={{ fontSize: "14px" }} />
                                                            {formatDate(item.date)}
                                                        </div>
                                                        <div className="d-flex align-items-center text-muted">
                                                            <FiFileText className="me-1" style={{ fontSize: "14px" }} />
                                                            {item.readTime}
                                                        </div>
                                                    </div>
                                                    {!isLocked && (
                                                        <button
                                                            className="btn btn-primary w"
                                                            onClick={() => handleOpenFeed(item)}
                                                        // style={{
                                                        //     background: "linear-gradient(135deg, #1bafb4 0%, #06b6a4 100%)",
                                                        //     border: "none",
                                                        //     borderRadius: "8px",
                                                        //     padding: "10px",
                                                        // }}
                                                        >
                                                            Read More
                                                            {/* <FiArrowRight className="ms-2" /> */}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
                            <div className="card-body p-3">
                                <div className="d-flex flex-wrap gap-2">
                                    {categories.map((category) => (
                                        <button
                                            key={category.id}
                                            className={`btn tab-button fw-semibold ${activeTab === category.id
                                                ? "btn-primary text-white"
                                                : "btn-light text-dark"
                                                }`}
                                            onClick={() => setActiveTab(category.id)}
                                            style={{
                                                borderRadius: "8px",
                                                padding: "8px 16px",
                                                fontSize: "13px",
                                                border: activeTab === category.id ? "none" : "1px solid #e9ecef",
                                                background: activeTab === category.id
                                                    ? "linear-gradient(135deg, #1bafb4 0%, #06b6a4 100%)"
                                                    : "white",
                                            }}
                                        >
                                            {category.name}
                                            <span
                                                className={`badge ms-2 ${activeTab === category.id
                                                    ? ""
                                                    : " text-dark"
                                                    }`}
                                                style={{ fontSize: "11px", padding: "2px 8px" }}
                                            >
                                                {category.count}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div
                                className="card-header border-0 d-flex justify-content-between align-items-center py-3 px-4"
                                style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}
                            >
                                <h4 className="text-primary">Research Feed</h4>
                                <span
                                    className="badge px-3 py-2"
                                    style={{
                                        background: "linear-gradient(135deg, #1bafb4 0%, #06b6a4 100%)",
                                        fontSize: "12px",
                                    }}
                                >
                                    {filteredFeed.length} Articles
                                </span>
                            </div>
                            <div className="card-body p-0">
                                {filteredFeed.map((item, index) => {
                                    const isLocked = item.isLocked && !canAccessContent(item.isLocked);
                                    return (
                                        <div
                                            key={item.id}
                                            className={`p-4 ${index !== filteredFeed.length - 1 ? "border-bottom" : ""}`}
                                            style={{
                                                background: isLocked ? "#c8eeecbf" : "white",
                                                transition: "all 0.2s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isLocked) {
                                                    e.currentTarget.style.background = "#f8fafc";
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = isLocked ? "#c8eeecbf" : "white";
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1 me-3">
                                                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                                        <span className="category-badge">{item.category}</span>
                                                        {isLocked ? (
                                                            <span className="premium-badge">
                                                                {/* <FiLock className="me-1" style={{ fontSize: "10px" }} /> */}
                                                                Premium
                                                            </span>
                                                        ) : (
                                                            <span
                                                                className="badge bg-success"
                                                            >
                                                                Free
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h6 className="mb-2 fw-bold" style={{ fontSize: "16px", lineHeight: "1.4" }}>
                                                        {item.title}
                                                    </h6>
                                                    <p className="text-muted mb-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                                                        {item.preview}
                                                    </p>
                                                    <div className="d-flex align-items-center gap-4 flex-wrap" style={{ fontSize: "12px" }}>
                                                        <div className="d-flex align-items-center text-muted">
                                                            <FiUser className="me-1" style={{ fontSize: "14px" }} />
                                                            {item.author}
                                                        </div>
                                                        <div className="d-flex align-items-center text-muted">
                                                            <FiCalendar className="me-1" style={{ fontSize: "14px" }} />
                                                            {formatDate(item.date)}
                                                        </div>
                                                        <div className="d-flex align-items-center text-muted">
                                                            <FiFileText className="me-1" style={{ fontSize: "14px" }} />
                                                            {item.readTime}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="d-flex flex-column gap-2" style={{ minWidth: "120px" }}>
                                                    {isLocked ? (
                                                        <button
                                                            className="btn btn-primary btn-sm fw-semibold"
                                                            onClick={handleUpgradePlan}
                                                            style={{
                                                                background: "linear-gradient(135deg, #1bafb4 0%, #06b6a4 100%)",
                                                                border: "none",
                                                                borderRadius: "8px",

                                                            }}
                                                        >
                                                            {/* <FiLock className="me-1" /> */}
                                                            Unlock
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn btn-primary btn-sm fw-semibold"
                                                            onClick={() => handleOpenFeed(item)}
                                                            style={{
                                                                background: "linear-gradient(135deg, #1bafb4 0%, #06b6a4 100%)",
                                                                border: "none",
                                                                borderRadius: "8px",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            Open
                                                            {/* <FiArrowRight className="ms-1" /> */}
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        title="Bookmark"
                                                        style={{ borderRadius: "8px" }}
                                                    >
                                                        {/* <FiBookmark /> */}
                                                        Bookmark
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Plan CTA Section */}
                        {subscriptionPlan === "free" && (
                            <div
                                className="card shadow-lg border-0 mb-4"
                                style={{
                                    background: "#c8eeecbf",
                                    borderRadius: "16px",
                                    overflow: "hidden",
                                }}
                            >
                                <div className="card-body p-5">
                                    <div className="row align-items-center">
                                        <div className="col-md-8 text-white">
                                            <div className="d-flex align-items-center mb-3">
                                                <div
                                                    className="me-3 d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: "56px",
                                                        height: "56px",
                                                        borderRadius: "14px",
                                                        background: "#c8eeecbf",
                                                        backdropFilter: "blur(10px)",
                                                    }}
                                                >
                                                    <FiZap style={{ fontSize: "28px", color: "#39b2b4" }} />
                                                </div>
                                                <h4 className="text-primary">Unlock Premium Research</h4>
                                            </div>
                                            <p className="mb-4" style={{ fontSize: "16px", opacity: 0.95 }}>
                                                Get access to exclusive research reports, in-depth analysis,
                                                expert insights, and premium content. Upgrade your plan today!
                                            </p>
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <div
                                                            className="me-2 d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "24px",
                                                                height: "24px",
                                                                borderRadius: "50%",
                                                                background: "#c8eeecbf",
                                                            }}
                                                        >
                                                            <FiCheck style={{ fontSize: "14px", color: " #06b6a4" }} />
                                                        </div>
                                                        <span style={{ fontSize: "14px", color: " #7184ad " }}>
                                                            Access to all premium research reports
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <div
                                                            className="me-2 d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "24px",
                                                                height: "24px",
                                                                borderRadius: "50%",
                                                                background: "#c8eeecbf",
                                                            }}
                                                        >
                                                            <FiCheck style={{ fontSize: "14px", color: " #06b6a4" }} />
                                                        </div>
                                                        <span style={{ fontSize: "14px", color: " #7184ad " }}>
                                                            Expert analyst calls and recommendations
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <div
                                                            className="me-2 d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "24px",
                                                                height: "24px",
                                                                borderRadius: "50%",
                                                                background: "#c8eeecbf",
                                                            }}
                                                        >
                                                            <FiCheck style={{ fontSize: "14px", color: " #06b6a4" }} />
                                                        </div>
                                                        <span style={{ fontSize: "14px", color: " #7184ad " }}>
                                                            Model portfolio allocations
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <div
                                                            className="me-2 d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "24px",
                                                                height: "24px",
                                                                borderRadius: "50%",
                                                                background: "#c8eeecbf",
                                                            }}
                                                        >
                                                            <FiCheck style={{ fontSize: "14px", color: " #06b6a4" }} />
                                                        </div>
                                                        <span style={{ fontSize: "14px", color: " #7184ad " }}>
                                                            Priority alerts and notifications
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4 text-center text-md-end mt-4 mt-md-0">
                                            <button
                                                className="btn btn-primary btn-lg "
                                                onClick={handleUpgradePlan}
                                                style={{
                                                    borderRadius: "12px",
                                                    fontSize: "16px",
                                                }}
                                            >
                                                {/* <FiStar className="me-2" style={{ color: "#f59e0b" }} /> */}
                                                Upgrade Plan
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                   
            </div>

        </>
    );
}
